import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTableFilters, IDataTable } from './interfaces';
import {
  DataGrid,
  GridColumnHeaderParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { enUS, esES } from '@mui/x-data-grid/locales';
import { Paper } from '@mui/material';
import { Toolbar } from '../Toolbar';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Header } from './components/Header';
import { HeaderFilterButton } from './components/HeaderFilterButton';
import {
  readObjectFromLocalStorage,
  storeDataInLocalStorage,
} from '../../../helpers/localStorageHelper';

export const DataTable: React.FC<IDataTable> = ({
  rows,
  columns,
  rowCount,
  isLoading,
  sortModel,
  paginationModel,
  initialState,
  sx,
  searchPlaceholder,
  storageKey,
  searchable,
  searchQuery,
  hasActiveFilters,
  getHeaderFilters,
  getRowHeight,
  getRowClassName,
  onPaginationModelChange,
  onSortModelChange,
  onRowClick,
  onSearch,
  onClearFilters,
}): JSX.Element => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const filters: DataTableFilters = getHeaderFilters ? getHeaderFilters() : {};

  const handleOnSearch = (search: string) => {
    if (onSearch) {
      onSearch(search);
    }
  };

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);

    if (storageKey) {
      storeDataInLocalStorage(storageKey, JSON.stringify(newModel));
    }
  };

  const customToolbar = useCallback(
    () => (
      <Toolbar
        searchPlaceholder={searchPlaceholder}
        onSearch={handleOnSearch}
        onClearFilters={onClearFilters}
        searchQuery={searchQuery}
        searchable={searchable}
        hasActiveFilters={hasActiveFilters}
      />
    ),
    [hasActiveFilters]
  );

  useEffect(() => {
    if (storageKey) {
      const savedColumnVisibilityModel = readObjectFromLocalStorage(storageKey);

      if (savedColumnVisibilityModel) {
        setColumnVisibilityModel(
          savedColumnVisibilityModel as GridColumnVisibilityModel
        );
      }
    }
  }, []);

  const updatedColumns = useMemo(() => {
    return columns.map((column) => {
      const filter = filters[column.field];

      if (!filter) return column;

      const filterComponent = filter.component;

      return {
        ...column,
        renderHeader: (params: GridColumnHeaderParams) => (
          <Header label={params.colDef.headerName || ''}>
            <HeaderFilterButton icon={FilterAltIcon} active={filter.active}>
              {filterComponent}
            </HeaderFilterButton>
          </Header>
        ),
      };
    });
  }, [filters, columns]);

  return (
    <Paper>
      <DataGrid
        rows={rows}
        showToolbar
        slots={{
          toolbar: customToolbar,
        }}
        columns={updatedColumns}
        getRowClassName={getRowClassName}
        loading={isLoading}
        disableColumnMenu={true}
        disableRowSelectionOnClick={true}
        rowCount={rowCount}
        localeText={
          (currentLocale === 'en' ? enUS : esES).components.MuiDataGrid
            .defaultProps.localeText
        }
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={false}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onRowClick={onRowClick}
        initialState={initialState}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        getRowHeight={getRowHeight}
        sx={{
          ...sx,
          border: 0,
          mb: '50px',
          '& .MuiTablePagination-input': {
            display: 'flex',
          },
          '& .MuiTablePagination-selectLabel': {
            display: 'block',
          },
          '& .MuiDataGrid-toolbar': {
            background: '#f9f9f9',
          },
          '& .MuiDataGrid-columnHeader': {
            background: '#FFF !important',
          },
          '& .MuiDataGrid-row': {
            background: '#FFF',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            background: '#FFF',
          },
          '& .MuiTablePagination-root': {
            background: '#f9f9f9',
          },
          '& .MuiDataGrid-filler': {
            background: '#FFF',
          },
          '& .MuiDataGrid-cellEmpty': {
            background: '#FFF',
          },
          // '.MuiDataGrid-columnHeader:focus': {
          //   outline: 'none',
          // },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: onRowClick ? 'pointer' : undefined,
          },
        }}
      />
    </Paper>
  );
};
